import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ActionDropdown } from '@/components';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { toast } from '@/design-system/toaster';

import { parseWarehouseError, warehouseApi } from '../../api/warehouse.api';
import BulkPickingModal from '../../components/BulkPickingModal';
import {
  isSettled,
  type BulkPickingOutcome,
} from '../../components/bulkPicking';
import { ActionDialog } from '../../components/dialogs';
import {
  BulkBar,
  EmptyTable,
  ListSearch,
  ListShell,
} from '../../components/ListShell';
import { HeaderActions } from '../../components/pickers';
import {
  codeCls,
  Dash,
  dateCls,
  LinesQty,
  LoadError,
  MappedBadge,
  nameCls,
  OrderRefCell,
  PageHeader,
} from '../../components/ui';
import { fmtDateTime, fmtQty } from '../../format';
import { useCappedSelection } from '../../hooks/useCappedSelection';
import { WarehouseFilterPanel } from '../../components/WarehouseFilterPanel';
import { GOODS_ISSUE_FILTERS } from '../../filterFields';
import { useListQuery } from '../../hooks/useListQuery';
import { useLoad } from '../../hooks/useLoad';
import WarehousePrintModal, {
  type PrintTarget,
} from '../../print/WarehousePrintModal';
import { GOODS_ISSUE_STATUS, PICKING_TABS } from '../../status';
import type { GoodsIssueSummary } from '../../types';
import { useWarehouse } from '../../WarehouseContext';
import { WAREHOUSE_PATHS } from '../../paths';

type Tab = (typeof PICKING_TABS)[number];

const PickingListPage: React.FC = () => {
  const navigate = useNavigate();
  const { hubID, ready, refreshToken, refresh } = useWarehouse();
  const q = useListQuery<Tab>(PICKING_TABS, 'ALL', GOODS_ISSUE_FILTERS);
  const { api: filterApi, apiKey: filterKey } = q.filters;
  const search = q.search.committed || null;
  const [print, setPrint] = useState<PrintTarget | null>(null);
  const [deleting, setDeleting] = useState<GoodsIssueSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const selection = useCappedSelection();
  const { selected, setSelected, clear: clearSelection } = selection;
  /** The pickings the bulk editor is open on (null = closed). */
  const [bulkItems, setBulkItems] = useState<GoodsIssueSummary[] | null>(null);
  /** The refresh after the bulk editor must keep the rows it left selected. */
  const keepSelectionRef = useRef(false);

  const list = useLoad(
    (signal) =>
      warehouseApi.listGoodsIssues(
        {
          hubID,
          stage: 'PICKING',
          status: q.tab === 'ALL' ? null : q.tab,
          ...filterApi,
          search,
          page: q.page,
          limit: q.limit,
        },
        { signal }
      ),
    [hubID, q.tab, filterKey, search, q.page, q.limit, refreshToken],
    ready
  );
  // Tab counts: the list's filters (minus the tab) — never disagree with it.
  const summary = useLoad(
    (signal) =>
      warehouseApi.getGoodsIssueSummary(
        { hubID, stage: 'PICKING', ...filterApi, search },
        { signal }
      ),
    [hubID, filterKey, search, refreshToken],
    ready
  );

  useEffect(() => {
    clearSelection();
  }, [q.tab, q.page, filterKey, hubID, clearSelection]);
  useEffect(() => {
    if (keepSelectionRef.current) {
      keepSelectionRef.current = false;
      return;
    }
    clearSelection();
  }, [refreshToken, clearSelection]);

  const rows = useMemo(() => list.data?.data ?? [], [list.data]);
  const selectedRows = rows.filter((g) => selected.includes(g.id));

  /** Closed: saved / ready pickings leave the selection, the rest stay for a retry. */
  const bulkDone = (outcomes: BulkPickingOutcome[]) => {
    const settled = new Set(
      outcomes.filter((o) => isSettled(o.state)).map((o) => o.id)
    );
    keepSelectionRef.current = true;
    setSelected(selected.filter((id) => !settled.has(id)));
    setBulkItems(null);
    refresh();
  };

  const counts = useMemo(() => {
    const m = new Map<string, number>();
    summary.data?.byStatus.forEach((b) => m.set(b.key, b.count));
    return m;
  }, [summary.data]);
  const tabs = PICKING_TABS.map((k) => ({
    key: k,
    label: k === 'ALL' ? 'All' : GOODS_ISSUE_STATUS[k].label,
    count: summary.data
      ? k === 'ALL'
        ? summary.data.total
        : (counts.get(k) ?? 0)
      : null,
  }));

  const confirmDelete = async () => {
    if (!deleting || busy) return;
    setBusy(true);
    try {
      await warehouseApi.cancelGoodsIssue(deleting.id);
      toast.info('Picking dihapus', { description: deleting.code });
      setDeleting(null);
      refresh();
    } catch (err) {
      toast.error('Gagal menghapus picking', {
        description: parseWarehouseError(err).message,
      });
    } finally {
      setBusy(false);
    }
  };

  const columns = useMemo<TableDataColumn<GoodsIssueSummary>[]>(
    () => [
      {
        key: 'code',
        title: 'Picking order',
        render: (_v, g) => <span className={codeCls}>{g.code}</span>,
      },
      {
        key: 'order',
        title: 'Request / External ID',
        render: (_v, g) => (
          <OrderRefCell order={g.outboundOrder} reference={g.reference} />
        ),
      },
      {
        key: 'client',
        title: 'Client',
        render: (_v, g) => (
          <span className={nameCls}>
            {g.client?.name ?? `Client #${g.clientID}`}
          </span>
        ),
      },
      {
        key: 'customer',
        title: 'Customer',
        render: (_v, g) =>
          g.outboundOrder ? (
            <span className={nameCls}>{g.outboundOrder.recipientName}</span>
          ) : (
            <Dash />
          ),
      },
      {
        key: 'picker',
        title: 'Picker',
        render: (_v, g) =>
          g.pickerName ? (
            <span className={nameCls}>{g.pickerName}</span>
          ) : (
            <span className="text-[12px] text-text-soft-400">
              Belum di-assign
            </span>
          ),
      },
      {
        key: 'lines',
        title: 'Lines / Qty',
        align: 'right',
        render: (_v, g) => (
          <LinesQty lines={g.lineCount} qty={fmtQty(g.totalReserved)} />
        ),
      },
      {
        key: 'status',
        title: 'Status',
        render: (_v, g) => (
          <MappedBadge map={GOODS_ISSUE_STATUS} value={g.status} />
        ),
      },
      {
        key: 'hub',
        title: 'Hub',
        render: (_v, g) => (
          <span className={nameCls}>{g.hub?.name ?? `Hub #${g.hubID}`}</span>
        ),
      },
      {
        key: 'created',
        title: 'Created',
        render: (_v, g) => (
          <span className={dateCls}>{fmtDateTime(g.createdAt)}</span>
        ),
      },
      {
        key: 'actions',
        title: '',
        align: 'right',
        render: (_v, g) => (
          <ActionDropdown
            actions={[
              {
                key: 'view',
                label: 'View details',
                icon: 'ri-eye-line',
                onClick: () => navigate(`${WAREHOUSE_PATHS.picking}/${g.id}`),
              },
              {
                key: 'order',
                label: 'Buka outbound request',
                icon: 'ri-file-list-3-line',
                hidden: !g.outboundOrderID,
                onClick: () =>
                  navigate(
                    `${WAREHOUSE_PATHS.outboundRequest}/${g.outboundOrderID}`
                  ),
              },
              {
                key: 'print',
                label: 'Print picking list',
                icon: 'ri-printer-line',
                onClick: () => setPrint({ kind: 'picking', id: g.id }),
              },
              {
                key: 'delete',
                label: 'Delete picking',
                icon: 'ri-delete-bin-line',
                destructive: true,
                onClick: () => setDeleting(g),
              },
            ]}
          />
        ),
      },
    ],
    [navigate]
  );

  return (
    <div className="space-y-5 p-6">
      <PageHeader
        eyebrow="Warehouse · Outbound management · Goods issue"
        title="Picking"
        actions={<HeaderActions />}
      />
      <ListShell
        tabs={tabs}
        activeTab={q.tab}
        onTabChange={q.setTab}
        filters={
          <>
            <ListSearch
              value={q.search.value}
              onChange={q.search.setValue}
              placeholder="Search picking, order, picker…"
            />
            <WarehouseFilterPanel filters={q.filters} title="Filter picking" />
          </>
        }
        bulkBar={
          <BulkBar
            count={selectedRows.length}
            noun="picking dipilih"
            actionLabel={`Edit picking (${selectedRows.length})`}
            actionIcon="ri-pencil-line"
            capped={selection.capped}
            onClear={clearSelection}
            onAction={() => setBulkItems(selectedRows)}
          />
        }
        pagination={{
          page: q.page,
          limit: q.limit,
          total: list.data?.pagination.total ?? 0,
          onPageChange: q.setPage,
          onLimitChange: q.setLimit,
        }}
      >
        {list.error ? (
          <LoadError message={list.error.message} onRetry={list.reload} />
        ) : (
          <TableData
            size="sm"
            loading={list.loading || !ready}
            data={rows}
            columns={columns}
            selection={{
              selectedKeys: selected,
              onSelectionChange: setSelected,
              getRowKey: (g) => g.id,
              isRowSelectable: (g) =>
                g.status === 'RESERVED' || g.status === 'PICKED',
            }}
            onRowClick={(g) => navigate(`${WAREHOUSE_PATHS.picking}/${g.id}`)}
            emptyState={<EmptyTable title="Tidak ada picking" />}
          />
        )}
      </ListShell>
      <ActionDialog
        open={!!deleting}
        title="Delete picking"
        subtitle={deleting?.code}
        description="Reserve stok dilepas dan outbound request kembali Open."
        confirmLabel="Delete picking"
        tone="destructive"
        testId="picking-delete-dialog"
        busy={busy}
        onConfirm={confirmDelete}
        onClose={() => setDeleting(null)}
      />
      {bulkItems && <BulkPickingModal items={bulkItems} onDone={bulkDone} />}
      <WarehousePrintModal target={print} onClose={() => setPrint(null)} />
    </div>
  );
};

export default PickingListPage;
